import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertOctagon, Plus, CheckCircle2 } from "lucide-react";

const statusColors = {
  open: { color: "#F59E0B", bg: "rgba(245,158,11,0.15)" },
  in_progress: { color: "#3B82F6", bg: "rgba(59,130,246,0.15)" },
  resolved: { color: "#059669", bg: "rgba(5,150,105,0.15)" },
};

const Complaints = () => {
  const { receptionists } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    employee_id: "",
    patient_reference: "",
    description: "",
    resolution: "",
    status: "open",
  });

  const load = () => api.get("/complaints").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.description) return toast.error("Description required");
    try {
      await api.post("/complaints", form);
      toast.success("Complaint logged");
      setOpen(false);
      setForm({
        date: new Date().toISOString().slice(0, 10),
        employee_id: "",
        patient_reference: "",
        description: "",
        resolution: "",
        status: "open",
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const updateStatus = async (id, patch) => {
    await api.put(`/complaints/${id}`, patch);
    load();
  };

  const funnel = ["open", "in_progress", "resolved"];
  const columns = funnel.map((s) => ({
    key: s,
    items: rows.filter((r) => r.status === s),
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Patient Complaints</div>
          <h1 className="font-heading text-4xl text-white tracking-tight mt-1">Resolution Funnel</h1>
          <p className="text-slate-400 mt-2 max-w-2xl">
            Every complaint has its own record and moves through Open → In Progress → Resolved.
            Alerts fire automatically for anything open beyond 48 hours.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-complaint-btn"
              className="bg-[#C9A24B] hover:bg-[#D4AF60] text-[#0F172A] font-medium h-11 px-5"
            >
              <Plus size={16} className="mr-2" /> Log Complaint
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#1E293B] border-[#334155] text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">New Complaint</DialogTitle>
              <DialogDescription className="text-slate-400 text-sm">
                Every complaint gets its own record. Items open beyond 48 hours are flagged.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date">
                <Input
                  type="date"
                  data-testid="complaint-date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="bg-[#0F172A] border-[#334155] text-white"
                />
              </Field>
              <Field label="Receptionist">
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger
                    data-testid="complaint-emp"
                    className="bg-[#0F172A] border-[#334155] text-white"
                  >
                    <SelectValue placeholder="Optional" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                    {receptionists.map((r) => (
                      <SelectItem key={r.employee_id} value={r.employee_id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Patient Reference">
              <Input
                data-testid="complaint-patient"
                value={form.patient_reference}
                onChange={(e) => setForm({ ...form, patient_reference: e.target.value })}
                className="bg-[#0F172A] border-[#334155] text-white"
              />
            </Field>
            <Field label="Description">
              <Textarea
                data-testid="complaint-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                className="bg-[#0F172A] border-[#334155] text-white"
              />
            </Field>
            <Button
              onClick={create}
              data-testid="complaint-save-btn"
              className="bg-[#C9A24B] hover:bg-[#D4AF60] text-[#0F172A] font-medium"
            >
              Save
            </Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {columns.map((col) => {
          const c = statusColors[col.key];
          return (
            <Card
              key={col.key}
              className="bg-[#1E293B] border-[#334155] p-5 min-h-[500px]"
              data-testid={`complaint-col-${col.key}`}
            >
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-[#334155]">
                <div className="flex items-center gap-2">
                  <AlertOctagon size={16} style={{ color: c.color }} />
                  <div className="font-heading text-lg text-white capitalize">
                    {col.key.replace("_", " ")}
                  </div>
                </div>
                <Badge
                  className="border font-mono"
                  style={{ backgroundColor: c.bg, color: c.color, borderColor: c.color }}
                >
                  {col.items.length}
                </Badge>
              </div>
              <div className="space-y-3">
                {col.items.map((item) => {
                  const overdue = col.key !== "resolved" && (item.age_hours || 0) >= 48;
                  return (
                    <div
                      key={item.complaint_id}
                      className={`p-3 rounded-lg bg-[#0F172A] border ${overdue ? "border-rose-500/60" : "border-[#334155]"}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="text-xs text-slate-500 font-mono">{item.date}</div>
                        {overdue && (
                          <Badge className="bg-rose-500/20 text-rose-400 border border-rose-500 text-[10px]">
                            48h+
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-white leading-snug">{item.description}</div>
                      {item.patient_reference && (
                        <div className="text-xs text-slate-500 mt-2">Patient: {item.patient_reference}</div>
                      )}
                      <div className="flex gap-2 mt-3">
                        {col.key === "open" && (
                          <Button
                            size="sm"
                            data-testid={`move-progress-${item.complaint_id}`}
                            onClick={() => updateStatus(item.complaint_id, { status: "in_progress" })}
                            className="h-7 text-xs bg-[#1E293B] hover:bg-[#334155] text-white border border-[#334155]"
                          >
                            Start
                          </Button>
                        )}
                        {col.key !== "resolved" && (
                          <Button
                            size="sm"
                            data-testid={`resolve-${item.complaint_id}`}
                            onClick={() =>
                              updateStatus(item.complaint_id, { status: "resolved", resolved: true })
                            }
                            className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500 text-white"
                          >
                            <CheckCircle2 size={12} className="mr-1" /> Resolve
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {col.items.length === 0 && (
                  <div className="text-xs text-slate-500 italic text-center py-8">Nothing here.</div>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase font-mono tracking-widest text-slate-400 mb-2 block">{label}</Label>
    {children}
  </div>
);

export default Complaints;
